import * as React from "react"
import { domToReact, } from "html-react-parser";

import HeaderImage from './headerImage'
import ReadAloudPassage from './readAloudPassage'

import { ReadAloudSource } from '../../../Shared/components/readAloud/readAloudSource'
import { CLIP_SOURCE_TYPE, clipSource } from '../../../Shared/libs/speech/clipSource'
import useFocus from '../../../Shared/hooks/useFocus'
import { ListenToggleButton, safeParseHtml } from '../../../Shared/index'
import {
  READ_PASSAGE_STEP,
  addPTagsToPassages,
  getStrippedPassageHighlights,
  onMobile,
  STUDENT_HIGHLIGHT_STARTS_TEXT,
  STUDENT_HIGHLIGHT_ENDS_TEXT
} from '../../helpers/containerActionHelpers'

const PASSAGE_HIGHLIGHT_STARTS_TEXT = "(yellow underlined text begins here)"
const PASSAGE_HIGHLIGHT_ENDS_TEXT = "(yellow underlined text ends here)"

const ReadPassageContainer = ({
  activities,
  activeStep,
  doneHighlighting,
  handleReadPassageContainerScroll,
  hasStartedPromptSteps,
  hasStartedReadPassageStep,
  scrolledToEndOfPassage,
  setScrolledToEndOfPassage,
  showReadTheDirectionsButton,
  session,
  studentHighlights,
  toggleStudentHighlight,
}) => {
  const lastMarkRef = React.useRef<HTMLElement | null>(null)
  const [containerRef, setContainerFocus] = useFocus()
  const [listenButtonRef, setListenButtonFocus] = useFocus()
  const [listening, setListening] = React.useState(false)
  const hasToggledListeningRef = React.useRef(false)
  const passageContentId = React.useId()

  React.useEffect(() => {
    if (scrolledToEndOfPassage || typeof IntersectionObserver === 'undefined') { return }

    const el = document.getElementById('end-of-passage')
    if (!el) { return }

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setScrolledToEndOfPassage(true) }
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [listening, scrolledToEndOfPassage])

  React.useEffect(() => {
    if (!hasToggledListeningRef.current) { return }

    setListenButtonFocus()
  }, [listening])

  function handleListenToggle() {
    hasToggledListeningRef.current = true
    setListening(!listening)
  }

  function handleListenExit() {
    hasToggledListeningRef.current = true
    setListening(false)
  }

  React.useEffect(() => {
    if (!showReadTheDirectionsButton) {
      setContainerFocus()
    }
  }, [showReadTheDirectionsButton])

  function handleHighlightKeyDown(e) {
    if (e.key !== 'Enter' && e.key !== ' ') { return }
    e.preventDefault()
    toggleStudentHighlight(e.currentTarget.textContent)
  }

  function handleHighlightClick(e) {
    toggleStudentHighlight(e.currentTarget.textContent)
  }

  function handleLastMarkFocus(e: React.FocusEvent<HTMLElement>) {
    if (e.currentTarget !== lastMarkRef.current) return
    
    setScrolledToEndOfPassage(true)
  }

  function stringifiedInnerElementsHelper(node) {
    if (node.data) { return node.data }
    if (node.children.length > 0) { return node.children.map(n => stringifiedInnerElementsHelper(n)).join(''); }
    return ''
  }

  function transformMarkTags(node) {
    const { activeStep } = session;
    const strippedPassageHighlights = getStrippedPassageHighlights({ activities, session, activeStep });

    if (['p'].includes(node.name) && activeStep > 1 && strippedPassageHighlights && strippedPassageHighlights.length) {
      const stringifiedInnerElements = node.children.map(n => stringifiedInnerElementsHelper(n)).join('');

      if (!stringifiedInnerElements) { return }

      const highlightIncludesElement = strippedPassageHighlights.find(ph => ph.includes(stringifiedInnerElements)) // handles case where passage highlight spans more than one paragraph
      const elementIncludesHighlight = strippedPassageHighlights.find(ph => stringifiedInnerElements.includes(ph)) // handles case where passage highlight is only part of paragraph

      if (highlightIncludesElement) {
        return (
          <p>
            <span className="passage-highlight">
              <span className="sr-only">{PASSAGE_HIGHLIGHT_STARTS_TEXT}</span>
              {stringifiedInnerElements}
              <span className="sr-only">{PASSAGE_HIGHLIGHT_ENDS_TEXT}</span>
            </span>
          </p>
        )
      }
      if (elementIncludesHighlight) {
        let newStringifiedInnerElements = stringifiedInnerElements
        strippedPassageHighlights.forEach(ph => newStringifiedInnerElements = newStringifiedInnerElements.replace(ph, `<span class="passage-highlight"><span class="sr-only">${PASSAGE_HIGHLIGHT_STARTS_TEXT}</span>${ph}<span class="sr-only">${PASSAGE_HIGHLIGHT_ENDS_TEXT}</span></span>`))
        return <p dangerouslySetInnerHTML={{ __html: newStringifiedInnerElements }} />
      }
    }

    if (node.name === 'mark') {
      const shouldBeHighlightable = !doneHighlighting && !showReadTheDirectionsButton && hasStartedReadPassageStep
      let innerElements = [domToReact(node.children, { replace: transformMarkTags, })]
      const stringifiedInnerElements = node.children.map(n => stringifiedInnerElementsHelper(n)).join('');
      let className = ''
      const highlighted = studentHighlights.includes(stringifiedInnerElements)
      if (activeStep === 1 && highlighted) {
        className += ' highlighted'
        const firstElement = <span className="sr-only">{STUDENT_HIGHLIGHT_STARTS_TEXT}</span>
        const lastElement = <span className="sr-only">{STUDENT_HIGHLIGHT_ENDS_TEXT}</span>
        innerElements = [firstElement, ...innerElements, lastElement]
      }
      className += shouldBeHighlightable ? ' highlightable' : ''
      if (!shouldBeHighlightable) { return <mark className={className}>{innerElements}</mark> }
      /* eslint-disable jsx-a11y/no-noninteractive-element-to-interactive-role */
      return (
        <mark
          aria-pressed={highlighted}
          className={className}
          onClick={handleHighlightClick}
          onFocus={handleLastMarkFocus}
          onKeyDown={handleHighlightKeyDown}
          ref={(el) => {
            if (el) lastMarkRef.current = el
          }}
          role="button"
          tabIndex={0}
        >
          {innerElements}
        </mark>
      )
      /* eslint-enable jsx-a11y/no-noninteractive-element-to-interactive-role */
    }
  }

  React.useEffect(() => {
    if (scrolledToEndOfPassage) return
    if (!hasStartedReadPassageStep) return
    if (showReadTheDirectionsButton) return

    // ensure the "last mark" ref points at the actual last <mark> after each render
    lastMarkRef.current = null
  }, [hasStartedReadPassageStep, scrolledToEndOfPassage, showReadTheDirectionsButton])

  const { currentActivity, } = activities
  if (!currentActivity) { return <span /> }

  const { title, passages, } = currentActivity
  const headerImage = passages[0].image_link && <img alt={passages[0].image_alt_text} className="header-image" src={passages[0].image_link} />
  let innerContainerClassName = "read-passage-inner-container "
  const innerPassageShouldBeBlurred = !hasStartedReadPassageStep || showReadTheDirectionsButton || (activeStep > READ_PASSAGE_STEP && !hasStartedPromptSteps)
  innerContainerClassName += innerPassageShouldBeBlurred ? 'blur' : ''

  if ((!hasStartedReadPassageStep || (activeStep > READ_PASSAGE_STEP && !hasStartedPromptSteps)) && onMobile()) {
    return <span />
  }
  const renderListenControls = (player = null) => {
    if (!player && innerPassageShouldBeBlurred) { return null }

    return (
      <ListenToggleButton buttonRef={listenButtonRef} controlsId={passageContentId} listening={listening} onToggle={handleListenToggle}>
        {player}
      </ListenToggleButton>
    )
  }

  if (listening) {
    return (
      <section aria-label="Passage" className="read-passage-container no-focus-outline" onScroll={handleReadPassageContainerScroll} ref={containerRef} tabIndex={-1}>
        <div aria-hidden={innerPassageShouldBeBlurred} className={innerContainerClassName} id={passageContentId}>
          <ReadAloudSource source={clipSource(CLIP_SOURCE_TYPE.EVIDENCE_ACTIVITY, currentActivity.id)}>
            <ReadAloudPassage
              controlsId={passageContentId}
              headerImage={<HeaderImage headerImage={headerImage} passage={passages[0]} />}
              onExit={handleListenExit}
              passages={passages}
              renderActionsBar={renderListenControls}
              title={title}
            />
          </ReadAloudSource>
        </div>
      </section>
    )
  }

  const formattedPassages = addPTagsToPassages(passages, scrolledToEndOfPassage)
  const formattedPassage = formattedPassages ? formattedPassages[0] : '';
  return (
    <section aria-label="Passage" className="read-passage-container no-focus-outline" onScroll={handleReadPassageContainerScroll} ref={containerRef} tabIndex={-1}>
      <div aria-hidden={innerPassageShouldBeBlurred} className={innerContainerClassName} id={passageContentId}>
        <h1 className="title">{safeParseHtml(title)}</h1>
        <HeaderImage headerImage={headerImage} passage={passages[0]} />
        {renderListenControls()}
        <div className="passage">{safeParseHtml(formattedPassage, { replace: transformMarkTags })}</div>
      </div>
    </section>
  )
}

export default ReadPassageContainer
